import { createZodFormResolver, lt, showSuccessAlert, useFeatureFlag } from '@mews/framework';
import { CommanderFormProps, useHandledDialogFormControls } from '@mews-commander/form';
import { Stack } from '@optimus-web/core';
import { RateGroupOptions, useRepositoryMutation } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { CentralPolicyFields } from '../../../../../rateGroups/components/forms/fields';
import { WithDefaultPolicy } from '../../../../../rateGroups/types';
import { useSelectedRateGroupContext } from '../../../../contexts/CurrentRateGroupContext';
import { editPaymentPolicySchema } from '../../../../../rateGroups/schemas/paymentPolicySchema';

export const EditDefaultPolicyDialogForm = ({ defaultPolicy, ...other }: WithDefaultPolicy & CommanderFormProps) => {
    const defaultPolicyFormResolver = createZodFormResolver(editPaymentPolicySchema);
    const { id: centralRateGroupId, bookablePeriod } = useSelectedRateGroupContext();
    const { options } = useSelectedRateGroupContext();
    const isPaymentDuringOCIRequired = useFeatureFlag(FeatureFlag.requirePaymentDuringOnlineCheckIn);

    const { mutateAsync } = useRepositoryMutation('portfolioCentralPaymentAndCancellationPolicies', 'set', {
        invalidateQueries: { portfolioCentralRateGroups: ['getAll', 'getOverview'], rateGroups: ['getAll'] },
        onSuccess: () => {
            showSuccessAlert({
                title: lt('RateGroupUpdatedSuccessMessage'),
            });
        },
    });

    const { renderFormContent } = useHandledDialogFormControls({
        ...other,
        values: {
            settlementRules: defaultPolicy.settlementRules.map(({ id }) => id),
            cancellationFees: defaultPolicy.cancellationPolicies.map(({ id }) => id),
            ...(isPaymentDuringOCIRequired
                ? {
                      requirePaymentCardDuringOnlineCheckIn: options.includes(
                          RateGroupOptions.RequirePaymentCardDuringOnlineCheckIn
                      ),
                  }
                : {}),
        },
        onSubmit: async (values) =>
            mutateAsync({
                centralCancellationPolicyIds: values.cancellationFees ?? [],
                centralPaymentPolicyIds: values.settlementRules,
                centralRateGroupId,
                ...(isPaymentDuringOCIRequired
                    ? { requirePaymentCardDuringOnlineCheckIn: values.requirePaymentCardDuringOnlineCheckIn ?? false }
                    : {}),
            }),
        resolver: defaultPolicyFormResolver,
    });

    return renderFormContent(
        <Stack spacing="300" vertical>
            <CentralPolicyFields bookablePeriod={bookablePeriod} />
        </Stack>
    );
};
